





































































import { Component, Vue, Watch } from "vue-property-decorator";
import { MiraSection, MiraSectionDocument } from "@/data/models/Mira";
import { loadMiraSections } from "@/data/repository/MiraRepository";
import { setMiraPageTitle } from "@/utils/MiraEdition";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
  COLOR_TABLE_HEADER,
  COLOR_TABLE_HEADER_DARK,
} from "@/utils/colors";
import { MiraCoverageStatus } from "@/utils/MiraDataQuality";
import { MiraViewContext, miraStatusColor } from "@/utils/MiraDataQualityView";
import { MiraUnlockEntry, MiraUnlockView, buildUnlockView } from "@/utils/MiraUnlockView";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";

/**
 * The route each section's figures open. The names mirror `ComponentName` in RouteGuards; they are
 * kept as literals (and navigation goes through the injected `$router`) so this page stays out of the
 * router's import graph, the same reason the Mira mount spec documents. All six sections have a screen.
 */
const SECTION_ROUTE_NAME: Record<MiraSection, string> = {
  [MiraSection.DATA_QUALITY]: "MiraDataQuality",
  [MiraSection.HYDRY]: "MiraDrying",
  [MiraSection.DEVICES]: "MiraDevices",
  [MiraSection.BUSINESS_DEVELOPMENT]: "MiraBusinessDevelopment",
  [MiraSection.PROCESS_FLOW]: "MiraProcessFlow",
  [MiraSection.INSURANCE]: "MiraInsurance",
};

/**
 * "Mehr Kennzahlen freischalten": the figures hidden across every Mira screen because the company
 * captures too little, grouped by the one thing each needs recorded, with how close it is and which
 * figures it would unlock. Motivates recording more data; a figure with a value lives on its screen,
 * so only the hidden ones are here.
 *
 * Always the last 12 months (no period picker). Gated by `VIEW_MIRA` like every Mira route. The rules
 * are in `@/utils/MiraUnlockView`. See `docs/analytics/mira-office-screens.md`.
 */
@Component
export default class MiraUnlockComponent extends Vue {
  isLoading = true;
  sections: Partial<Record<MiraSection, MiraSectionDocument>> = {};

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get barColor() {
    return themed(COLOR_MIRA_BAR, COLOR_MIRA_BAR_DARK);
  }

  get barTrackColor() {
    return themed(COLOR_MIRA_BAR_TRACK, COLOR_MIRA_BAR_TRACK_DARK);
  }

  /** A getter, not a field: a field is evaluated once and would not follow a theme toggle. */
  get goodColor() {
    return miraStatusColor(MiraCoverageStatus.GOOD, this.$vuetify.theme.dark);
  }

  get viewContext(): MiraViewContext {
    return {
      t: (key, params) => this.$t(key, params ?? []).toString(),
      te: (key) => this.$te(key),
      formatNumber: (value) => formatNumber(value),
      dark: this.$vuetify.theme.dark,
      locale: this.$i18n.locale,
    };
  }

  /** A single cached getter, so the view model is built once per render, not once per reader. */
  get view(): MiraUnlockView {
    return buildUnlockView(this.sections, this.viewContext);
  }

  get entries(): MiraUnlockEntry[] {
    return this.view.entries;
  }

  /** What the company can record, nearest first. */
  get recordEntries(): MiraUnlockEntry[] {
    return this.entries.filter((entry) => entry.kind === "RECORD");
  }

  /** The two parts of the page, each only when it has entries: tasks first, then figures waiting for cases. */
  get parts(): Array<{ kind: string; entries: MiraUnlockEntry[] }> {
    const cases = this.entries.filter((entry) => entry.kind === "CASES");
    return [
      { kind: "RECORD", entries: this.recordEntries },
      { kind: "CASES", entries: cases },
    ].filter((part) => part.entries.length > 0);
  }

  get waitingCount(): number {
    return this.view.waitingCount;
  }

  get headerDescription(): string {
    const period = this.$t("mira.periods.rolling12").toString();
    return `${period} · ${this.$tc("mira.unlock.waiting", this.waitingCount)}`;
  }

  fillWidth(fraction: number): string {
    return `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  }

  sectionName(section: MiraSection): string {
    return this.$t(`mira.sections.${section}`).toString();
  }

  openSection(section: MiraSection) {
    this.$router.push({ name: SECTION_ROUTE_NAME[section] });
  }

  /** The app bar's title, "Mira > Mehr Kennzahlen freischalten" (#2611027), again on a language change. */
  @Watch("$i18n.locale")
  setTitle() {
    setMiraPageTitle(this, "mira.unlock.title");
  }

  async mounted() {
    this.setTitle();

    try {
      this.sections = await loadMiraSections();
    } catch (error) {
      SnackbarModule.showError({ key: "mira.dataQuality.loadError" });
    } finally {
      this.isLoading = false;
    }
  }
}
